/**
 * Picklists manager — Manager+ only (PRD addendum Apr-2026).
 *
 * Configurable picklists for the three deal columns: ``deal_stage``,
 * ``deal_status``, ``deal_value``. Each column has a small set of
 * locked seed values (the close / reopen routers depend on them) and
 * any number of manager-added rows. The locked rows render as
 * read-only chips with a small "system" badge; manager-added rows can
 * be renamed, deactivated, reordered, or deleted.
 *
 * Layout: a tab selector picks which kind we're editing. The tab body
 * is a list of rows (active first, then inactive) plus a create bar
 * at the top. We don't show all three lists side-by-side because the
 * user will only ever edit one at a time, and the side-by-side layout
 * would be cramped on phones.
 *
 * Optimistic updates are deliberately avoided here — picklists are
 * tiny lists with very low write volume, and the server-side rules
 * (locked rows, label clashes, in-use deletes) are nuanced enough
 * that surfacing the real server response is more trustworthy.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

import {
  ApiError,
  PICKLIST_KINDS,
  PICKLIST_KIND_LABELS,
  type PicklistKind,
  type PicklistOption,
  picklistsApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

const VALUE_MAX = 64;
const LABEL_MAX = 64;

export const Route = createFileRoute("/_app/settings/picklists")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin" && user.role !== "manager") {
      throw redirect({ to: "/deals" });
    }
  },
  component: PicklistsSettingsPage,
});

function PicklistsSettingsPage() {
  const [expanded, setExpanded] = useState<PicklistKind | null>("deal_stage");
  const descriptions: Record<PicklistKind, string> = {
    deal_stage: "The current step in your sales process.",
    deal_status: "The working state of a deal.",
    deal_value: "Reusable value bands for deal reporting.",
    unit_of_measure: "Units used by products, raw materials, orders, and imports.",
  };

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">Picklists</h2>
        <p className="text-sm opacity-60">
          Customize the dropdowns reps see on deals — stage, status, and value. System-managed rows
          (locked) show with a small badge and can&apos;t be renamed or removed; everything else is
          yours to tune.
        </p>
      </header>

      <div className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
        {PICKLIST_KINDS.map((kind) => {
          const isExpanded = kind === expanded;
          return (
            <section key={kind}>
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => setExpanded(isExpanded ? null : kind)}
                className="flex min-h-16 w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
              >
                <span>
                  <span className="block text-sm font-semibold">{PICKLIST_KIND_LABELS[kind]}</span>
                  <span className="mt-0.5 block text-xs opacity-60">{descriptions[kind]}</span>
                </span>
                <ChevronDown
                  className={`size-4 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                  aria-hidden
                />
              </button>
              {isExpanded && (
                <div className="border-t border-[var(--border)] bg-black/[0.015] p-4 dark:bg-white/[0.02]">
                  <PicklistEditor kind={kind} />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function PicklistEditor({ kind }: { kind: PicklistKind }) {
  const queryClient = useQueryClient();
  const listQuery = useQuery({
    queryKey: ["picklists", kind],
    queryFn: () => picklistsApi.list(kind),
  });

  const items = listQuery.data?.items ?? [];
  const [activeRows, inactiveRows] = useMemo(() => {
    const a: PicklistOption[] = [];
    const i: PicklistOption[] = [];
    for (const row of items) {
      (row.is_active ? a : i).push(row);
    }
    return [a, i];
  }, [items]);

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: ["picklists", kind] });
    // Deal-side caches read the picklist for filter chips / kanban
    // headers — invalidate them too so a relabel propagates without
    // a refresh.
    queryClient.invalidateQueries({ queryKey: ["deals"] });
  };

  return (
    <div className="space-y-4">
      <CreatePicklistBar kind={kind} onCreated={onChanged} />

      {listQuery.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : listQuery.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load picklist: {(listQuery.error as Error).message}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm opacity-60">
          No options yet. Create one above to populate the dropdown.
        </p>
      ) : (
        <div className="space-y-4">
          <PicklistRows heading="Active" rows={activeRows} kind={kind} onChanged={onChanged} />
          {inactiveRows.length > 0 && (
            <PicklistRows
              heading="Inactive"
              rows={inactiveRows}
              kind={kind}
              onChanged={onChanged}
              dimmed
            />
          )}
        </div>
      )}
    </div>
  );
}

function PicklistRows({
  heading,
  rows,
  kind,
  onChanged,
  dimmed = false,
}: {
  heading: string;
  rows: PicklistOption[];
  kind: PicklistKind;
  onChanged: () => void;
  dimmed?: boolean;
}) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-60">
        {heading} <span className="opacity-50">({rows.length})</span>
      </h3>
      <ul
        className={`divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)] ${dimmed ? "opacity-70" : ""}`}
      >
        {rows.map((row) => (
          <PicklistRow key={row.id} option={row} kind={kind} onChanged={onChanged} />
        ))}
      </ul>
    </section>
  );
}

function CreatePicklistBar({
  kind,
  onCreated,
}: {
  kind: PicklistKind;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState("");
  const [valueDraft, setValueDraft] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The server normalises ``value`` to lower_with_underscores on POST.
  // We mirror it in the input as a hint so the manager isn't surprised
  // on save, but still let them override (e.g. ``QuotedV2``).
  const previewValue = useMemo(() => {
    if (valueDraft.trim()) return valueDraft.trim();
    return label.trim().toLowerCase().split(/\s+/).filter(Boolean).join("_");
  }, [label, valueDraft]);

  const createMutation = useMutation({
    mutationFn: () =>
      picklistsApi.create(kind, {
        value: previewValue,
        label: label.trim(),
        category: kind === "unit_of_measure" ? category.trim() || null : null,
      }),
    onSuccess: () => {
      setLabel("");
      setValueDraft("");
      setCategory("");
      setError(null);
      onCreated();
      toast.success("Option added.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not add option.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const l = label.trim();
    if (!l) {
      setError("Label is required.");
      return;
    }
    if (l.length > LABEL_MAX) {
      setError(`Labels are at most ${LABEL_MAX} characters.`);
      return;
    }
    if (!previewValue) {
      setError("Value is required.");
      return;
    }
    if (previewValue.length > VALUE_MAX) {
      setError(`Values are at most ${VALUE_MAX} characters.`);
      return;
    }
    setError(null);
    createMutation.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-2 rounded-xl border border-[var(--border)] p-4">
      <div
        className={`grid grid-cols-1 gap-3 ${
          kind === "unit_of_measure"
            ? "sm:grid-cols-[2fr_2fr_1.25fr_auto]"
            : "sm:grid-cols-[2fr_2fr_auto]"
        }`}
      >
        <label className="space-y-1 text-xs font-medium opacity-80">
          <span>Label (what reps see)</span>
          <input
            type="text"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setError(null);
            }}
            placeholder="e.g. Quoted"
            maxLength={LABEL_MAX}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            autoComplete="off"
          />
        </label>
        {kind === "unit_of_measure" && (
          <label className="space-y-1 text-xs font-medium opacity-80">
            <span>Category</span>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Mass"
              maxLength={LABEL_MAX}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
              autoComplete="off"
            />
          </label>
        )}
        <label className="space-y-1 text-xs font-medium opacity-80">
          <span>
            Stored value <span className="opacity-60">(optional — auto-derived from label)</span>
          </span>
          <input
            type="text"
            value={valueDraft}
            onChange={(e) => {
              setValueDraft(e.target.value);
              setError(null);
            }}
            placeholder={previewValue || "quoted"}
            maxLength={VALUE_MAX}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            autoComplete="off"
          />
        </label>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={createMutation.isPending || label.trim().length === 0}
            className="inline-flex h-[34px] w-full items-center justify-center gap-1.5 rounded-md bg-[var(--accent)] px-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 sm:w-auto"
          >
            {createMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Add option
          </button>
        </div>
      </div>
      {error && (
        <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
          <AlertCircle className="size-3" aria-hidden /> {error}
        </p>
      )}
      <p className="text-xs opacity-60">
        Tip: stored value is what reports filter on. Once an option is in use, you can rename the
        label freely; the stored value is fixed.
      </p>
    </form>
  );
}

function PicklistRow({
  option,
  kind,
  onChanged,
}: {
  option: PicklistOption;
  kind: PicklistKind;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(option.label);
  const [draftCategory, setDraftCategory] = useState(option.category ?? "");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const renameMutation = useMutation({
    mutationFn: (values: { label: string; category?: string | null }) =>
      picklistsApi.update(kind, option.id, values),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onChanged();
      toast.success("Renamed.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not rename.";
      setError(msg);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (next: boolean) => picklistsApi.update(kind, option.id, { is_active: next }),
    onSuccess: () => {
      onChanged();
      toast.success(option.is_active ? "Deactivated." : "Reactivated.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not update.";
      setError(msg);
      toast.error(msg);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (delta: number) =>
      picklistsApi.update(kind, option.id, {
        position: Math.max(0, option.position + delta),
      }),
    onSuccess: () => {
      onChanged();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not reorder.";
      toast.error(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => picklistsApi.remove(kind, option.id),
    onSuccess: () => {
      onChanged();
      toast.success("Deleted.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      toast.error(msg);
      setError(msg);
    },
  });

  const submitRename = () => {
    const next = draftLabel.trim();
    if (!next) {
      setError("Label can't be empty.");
      return;
    }
    const nextCategory = draftCategory.trim() || null;
    if (next === option.label && (kind !== "unit_of_measure" || nextCategory === option.category)) {
      setEditing(false);
      setError(null);
      return;
    }
    if (next.length > LABEL_MAX) {
      setError(`Labels are at most ${LABEL_MAX} characters.`);
      return;
    }
    renameMutation.mutate({
      label: next,
      ...(kind === "unit_of_measure" ? { category: nextCategory } : {}),
    });
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
      setDraftLabel(option.label);
      setDraftCategory(option.category ?? "");
      setError(null);
    }
  };

  const handleDelete = () => {
    if (option.is_locked) return;
    if (
      window.confirm(
        `Delete option “${option.label}”? Deals already using it will keep their value, but the option won't show in new dropdowns.`,
      )
    ) {
      removeMutation.mutate();
    }
  };

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/* Reorder handles. Locked rows can't be reordered (their
            position is part of the seed contract). */}
        {!option.is_locked && (
          <div className="flex flex-col">
            <button
              type="button"
              aria-label="Move up"
              disabled={reorderMutation.isPending}
              onClick={() => reorderMutation.mutate(-15)}
              className="rounded p-0.5 hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              <ChevronUp className="size-3" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={reorderMutation.isPending}
              onClick={() => reorderMutation.mutate(15)}
              className="rounded p-0.5 hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              <ChevronDown className="size-3" aria-hidden />
            </button>
          </div>
        )}

        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="text"
                value={draftLabel}
                onChange={(e) => {
                  setDraftLabel(e.target.value);
                  setError(null);
                }}
                onKeyDown={onKey}
                maxLength={LABEL_MAX}
                ref={inputRef}
                className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              />
              {kind === "unit_of_measure" && (
                <input
                  type="text"
                  value={draftCategory}
                  onChange={(e) => setDraftCategory(e.target.value)}
                  onKeyDown={onKey}
                  maxLength={LABEL_MAX}
                  aria-label="Category"
                  placeholder="Category"
                  className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)] sm:w-32"
                />
              )}
              <button
                type="button"
                onClick={submitRename}
                disabled={renameMutation.isPending}
                aria-label="Save"
                className="rounded-md border border-emerald-300 p-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
              >
                {renameMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <Check className="size-3.5" aria-hidden />
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraftLabel(option.label);
                  setDraftCategory(option.category ?? "");
                  setError(null);
                }}
                aria-label="Cancel"
                disabled={renameMutation.isPending}
                className="rounded-md border border-[var(--border)] p-1 hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{option.label}</span>
              <span className="rounded-full bg-black/5 px-1.5 py-0.5 font-mono text-[11px] opacity-70 dark:bg-white/10">
                {option.value}
              </span>
              {kind === "unit_of_measure" && option.category && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-200">
                  {option.category}
                </span>
              )}
              {option.is_locked && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                  title="System-managed value — cannot be renamed or deleted."
                >
                  <Lock className="size-3" aria-hidden /> system
                </span>
              )}
              {option.is_terminal && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800 dark:bg-red-950/40 dark:text-red-200"
                  title="Terminal value — closes the deal when set."
                >
                  closes deal
                </span>
              )}
              {!option.is_active && (
                <span className="rounded-full bg-black/5 px-2 py-0.5 text-[11px] opacity-70 dark:bg-white/10">
                  inactive
                </span>
              )}
            </div>
          )}
          {error && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>}
        </div>
      </div>

      {!editing && (
        <div className="flex shrink-0 items-center gap-1">
          {!option.is_locked && (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditing(true);
                  setDraftLabel(option.label);
                  setDraftCategory(option.category ?? "");
                  setError(null);
                }}
                aria-label={`Rename ${option.label}`}
                className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
              >
                <Pencil className="size-3" aria-hidden /> Rename
              </button>
              <button
                type="button"
                onClick={() => toggleActiveMutation.mutate(!option.is_active)}
                disabled={toggleActiveMutation.isPending}
                aria-label={option.is_active ? "Deactivate" : "Reactivate"}
                className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
              >
                {toggleActiveMutation.isPending ? (
                  <Loader2 className="size-3 animate-spin" aria-hidden />
                ) : option.is_active ? (
                  <EyeOff className="size-3" aria-hidden />
                ) : (
                  <Eye className="size-3" aria-hidden />
                )}
                {option.is_active ? "Hide" : "Show"}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={removeMutation.isPending}
                aria-label={`Delete ${option.label}`}
                className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
              >
                {removeMutation.isPending ? (
                  <Loader2 className="size-3 animate-spin" aria-hidden />
                ) : (
                  <Trash2 className="size-3" aria-hidden />
                )}
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}
